import './doughnutChartRegistry'
import type { Chart as ChartJS } from 'chart.js'
import type { ChartData } from 'chart.js'
import { Doughnut } from 'react-chartjs-2'
import { Box } from '@mui/material'

const centerTextPlugin = {
  id: 'centerText',
  afterDraw: (chart: ChartJS) => {
    if ((chart.config as { type?: string })?.type !== 'doughnut') return
    const { ctx, chartArea } = chart
    if (!chartArea) return
    const dataset = chart.data.datasets[0]
    if (!dataset?.data) return
    const total = (dataset.data as number[]).reduce((a, b) => a + b, 0)
    const centerX = (chartArea.left + chartArea.right) / 2
    const centerY = (chartArea.top + chartArea.bottom) / 2
    ctx.save()
    ctx.font = 'bold 24px sans-serif'
    ctx.fillStyle = '#000'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillText(String(total), centerX, centerY)
    ctx.restore()
  },
}

const segmentLabelsPlugin = {
  id: 'segmentLabels',
  afterDraw: (chart: ChartJS) => {
    if ((chart.config as { type?: string })?.type !== 'doughnut') return
    const { ctx, data } = chart
    const meta = chart.getDatasetMeta(0)
    if (!meta?.data?.length || !data.datasets[0]?.data) return
    const values = data.datasets[0].data as number[]
    const total = values.reduce((a, b) => a + b, 0)
    meta.data.forEach((arc: unknown, i: number) => {
      const a = arc as {
        startAngle: number
        endAngle: number
        x: number
        y: number
        outerRadius: number
        innerRadius: number
      }
      const value = values[i] ?? 0
      const pct = total > 0 ? ((100 * value) / total).toFixed(2) : '0'
      const angle = (a.startAngle + a.endAngle) / 2
      const r = (a.outerRadius + a.innerRadius) / 2
      const x = a.x + Math.cos(angle) * r
      const y = a.y + Math.sin(angle) * r
      ctx.save()
      ctx.font = '14px sans-serif'
      ctx.fillStyle = '#333'
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      ctx.fillText(String(value), x, y - 8)
      ctx.font = '11px sans-serif'
      ctx.fillStyle = '#666'
      ctx.fillText(`${pct}%`, x, y + 6)
      ctx.restore()
    })
  },
}

const DOUGHNUT_COLORS = ['#FFB2AA', '#4DC7DD', '#FFCC66']

const defaultData: ChartData<'doughnut'> = {
  labels: ['Легковые', 'Грузовые', 'Неопознанные'],
  datasets: [
    {
      data: [57, 91, 37],
      backgroundColor: DOUGHNUT_COLORS,
      borderWidth: 0,
    },
  ],
}

const LEGEND_GAP_PX = 28

const getOptions = () => ({
  responsive: true,
  maintainAspectRatio: true,
  cutout: '40%',
  plugins: {
    legend: {
      display: false,
    },
  },
})

type DoughnutChartProps = {
  data?: ChartData<'doughnut'>
  height?: number
  totalVehicleCount: number
  passengerVehicleCount: number
  truckVehicleCount: number
  unrecognizedVehicleCount: number
}

const DoughnutChart = ({
  data,
  height = 400,
  totalVehicleCount,
  passengerVehicleCount,
  truckVehicleCount,
  unrecognizedVehicleCount,
}: DoughnutChartProps) => {
  const defaultData: ChartData<'doughnut'> = {
    labels: ['Легковые', 'Грузовые', 'Неопознанные'],
    datasets: [
      {
        data: [
          passengerVehicleCount,
          truckVehicleCount,
          unrecognizedVehicleCount,
        ],
        backgroundColor: DOUGHNUT_COLORS,
        borderWidth: 0,
      },
    ],
  }
  const labels = defaultData.labels ?? []
  const colors = (defaultData.datasets?.[0]?.backgroundColor ??
    DOUGHNUT_COLORS) as string[]

  return (
    <Box
      sx={{
        width: 340,
        height: 380,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        // mt: `${LEGEND_GAP_PX}px`,
      }}
    >
      <Box sx={{ flex: 1, width: '100%', minHeight: 240 }}>
        <Doughnut
          data={defaultData}
          options={getOptions()}
          plugins={[centerTextPlugin, segmentLabelsPlugin] as any}
        />
      </Box>
      <Box
        component='ul'
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
          gap: 2,
          mt: `${LEGEND_GAP_PX}px`,
          listStyle: 'none',
          p: 0,
          m: 0,
        }}
      >
        {(labels as string[]).map((label, i) => (
          <Box
            component='li'
            key={String(label) + i}
            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
          >
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                bgcolor: colors[i] ?? '#ccc',
                flexShrink: 0,
              }}
            />
            <Box
              component='span'
              sx={{ fontSize: 12, color: 'text.secondary' }}
            >
              {label}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  )
}

export default DoughnutChart
